import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion"; // Add this line
import api from "../../api/axios";

export default function AutoSlider() {
  const [sliders, setSliders] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const touchStartX = useRef(0);
  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  // 1. Fetch Sliders - Only runs once on mount
  useEffect(() => {
    const fetchSliders = async () => {
      try {
        const res = await api.get('/sliders');
        if (res.data.success) {
          setSliders(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching Sliders:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSliders();
  }, []);

  // 2. Navigation Logic - Memoized to prevent re-renders
  const nextSlide = useCallback(() => {
    if (sliders.length === 0) return;
    setCurrent((prev) => (prev + 1) % sliders.length);
  }, [sliders.length]);

  const prevSlide = useCallback(() => {
    if (sliders.length === 0) return;
    setCurrent((prev) => (prev === 0 ? sliders.length - 1 : prev - 1));
  }, [sliders.length]);

  // 3. Autoplay Logic - Resets when user clicks or pauses
  useEffect(() => {
    if (paused || loading || sliders.length === 0) return;

    const timer = setInterval(() => {
      nextSlide();
    }, 4000); // 4 seconds for a better user experience

    return () => clearInterval(timer);
  }, [paused, loading, sliders.length, nextSlide]);

  // 4. Image URL Helper
  const getImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${BACKEND_BASE}/storage/${path}`;
  };

  // 5. Touch Handlers
  const handleTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX.current - touchEndX;
    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
  };

  if (loading) return (
    <div className="flex justify-center items-center h-[37vw] bg-gray-50">
      <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
    </div>
  );

  if (sliders.length === 0) return null;

  return (
    <div
      // className="relative w-full h-[37vw] md:h-[37.2vw] overflow-hidden bg-gray-100 group"
      className="relative w-full h-[auto] md:h-[auto] overflow-hidden bg-gray-100 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Container flex pour le défilement */}
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {sliders.map((slide, index) => (
          <div key={slide.id || index} className="min-w-full h-full relative">
            <img
              src={getImageUrl(slide.image)}
              alt={slide.title || "Titan Slider"}
              className="w-full h-full object-cover select-none"
              draggable="false"
            />
            {/* Optional Overlay for Text if your slider has it */}
            {slide.title && (
               <div className="absolute inset-0 bg-black/10 flex items-center px-12">
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={current === index ? { opacity: 1, x: 0 } : {}}
                    className="text-white text-4xl font-black uppercase"
                  >
                    {slide.title}
                  </motion.h2>
               </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Controls - Hidden on mobile, show on hover on Desktop */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-[#ffaa00] z-10"
      >
        <ChevronLeft size={24} />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-[#ffaa00] z-10"
      >
        <ChevronRight size={24} />
      </button>

      {/* Pagination Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {sliders.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-1.5 transition-all rounded-full ${
              current === i ? "w-8 bg-[#ffaa00]" : "w-2 bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}